import React from "react";
import { IInscription } from "../../../../../models/interfaces/IInscription";
import { FormState, useFormContext } from "react-hook-form";
import CpntFormSection from "../../../../../components/forms/CpntFormSection";
import CpntFormSectionContainer from "../../../../../components/forms/CpntFormSectionContainer";
import SelectInput from "../../../../../components/forms/SelectInput";
import CheckboxInput from "../../../../../components/forms/CheckBoxInput";
import { TYPES_COMPTE_Inscription, validationMode } from "../../../../../config/helpers";

interface InscriptionFormConditionsProps {
  data: IInscription;
  disabled: boolean;
  showButtons: boolean;
  isUpdating: boolean;
  isSaving?: boolean;
  readOnly: boolean;
  onAnnuler?: () => void;
  onSave?: (formData: IInscription) => Promise<boolean>;
  onValider: () => void;
  onRefuser?: () => void;
  sirenApiError?: string | null;
  emailApiError?: string | null;
  checkEmail?: (emailToCheck?: string) => Promise<boolean>;
  isCheckingEmail?: boolean;
  formState?: FormState<IInscription>;
}

const InscriptionFormConditions: React.FC<InscriptionFormConditionsProps> = ({
  data,
  disabled,
  showButtons,
  isUpdating,
  isSaving = false,
  readOnly,
  onAnnuler,
  onSave,
  onValider,
  onRefuser,
  sirenApiError,
  emailApiError,
  checkEmail,
  isCheckingEmail = false
}) => {
  const { getValues, reset, trigger } = useFormContext<IInscription>();
  
  const handleSaveClick = async () => {
    if (onSave) {
      // Déclencher la validation de tous les champs obligatoires
      const isValid = await trigger();
      
      // Si la validation échoue, arrêter ici (les champs en erreur s'afficheront en rouge)
      if (!isValid) {
        return;
      }
      
      // Vérifier s'il y a une erreur API sur le SIREN (SIREN déjà en base, non diffusible, etc.)
      if (sirenApiError) {
        return;
      }
      
      // Vérifier l'email avant d'enregistrer
      if (checkEmail) {
        const emailIsValid = await checkEmail();
        if (!emailIsValid) {
          return; // Ne pas enregistrer si l'email existe déjà
        }
      }
      
      // Vérifier s'il y a une erreur API sur l'email (email déjà utilisé)
      if (emailApiError) {
        return;
      }
      
      const formData = getValues();
      await onSave(formData);
    }
  };
  
  const handleAnnulerClick = () => {
    // Réinitialiser le formulaire aux valeurs par défaut (celles chargées depuis l'API)
    reset();
    // Appeler le callback onAnnuler si fourni
    if (onAnnuler) {
      onAnnuler();
    }
  };

  return (
    <div className="card mb-3">
      <div className="card-header">
        <h6 className="mb-0 fw-bold">CONDITIONS ET TYPE DE COMPTE</h6>
      </div>
      <div className="card-body">
        <CpntFormSection pSectionLabel="">
          <CpntFormSectionContainer ColumnsSize={12} ColumnsSizesMd={[12]}>
            <CheckboxInput
              formName="AcceptationCGU"
              label="J’accepte les conditions générales de vente et d'utilisation"
              options={[{ value: "disabled", defaultChecked: data.AcceptationCGU === true }]}
              labelRight={true}
            />
          </CpntFormSectionContainer>

          <CpntFormSectionContainer ColumnsSize={12} ColumnsSizesMd={[12]}>
            <SelectInput
              label="Type de compte"
              formName="IdTypeDeCompteSociete"
              ismandatory={true}
              optionEmpty="Sélectionnez un type"
              validationStyle={validationMode.classic}
              options={TYPES_COMPTE_Inscription}
              disabled={disabled}
            />
          </CpntFormSectionContainer>

          {showButtons && (
            <>
              <CpntFormSectionContainer ColumnsSize={12} ColumnsSizesMd={[6, 6]}>
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  onClick={handleAnnulerClick}
                  disabled={readOnly || isUpdating || isSaving}
                  style={{ padding: "12px" }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={handleSaveClick}
                  disabled={readOnly || isUpdating || isSaving}
                  style={{ padding: "12px" }}
                >
                  {isSaving ? "Enregistrement..." : "Enregistrer"}
                </button>
              </CpntFormSectionContainer>

              <CpntFormSectionContainer ColumnsSize={12} ColumnsSizesMd={[6, 6]}>
                <button
                  type="button"
                  className="btn btn-success w-100"
                  onClick={onValider}
                  disabled={readOnly || isUpdating || isSaving}
                  style={{ padding: "12px" }}
                >
                  {isUpdating ? "Traitement..." : "Validation du compte"}
                </button>
                <button
                  type="button"
                  className="btn btn-danger w-100"
                  onClick={onRefuser}
                  disabled={readOnly || isUpdating || isSaving}
                  style={{ padding: "12px" }}
                >
                  {isUpdating ? "Traitement..." : "Refus du compte"}
                </button>
              </CpntFormSectionContainer>
            </>
          )}
        </CpntFormSection>
      </div>
    </div>
  );
};

export default InscriptionFormConditions;
